import { CheckCircle2, Circle, Clock, X } from 'lucide-react'
import { Card, StatusBadge } from '@shared/design-system/ui'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import type { KycOnboarding } from '../api/verification'
import { onboardingOperationStatus } from '../model/onboarding-status'
import { formCard, sectionCopy, sectionHeader, sectionTitle } from './onboarding.css'
import {
  copy,
  section,
  statusDescription,
  statusItem,
  statusList,
  statusMarker,
  statusText,
  statusTitle,
  statusValue,
} from './operational-status.css'

type MarkerTone = 'ok' | 'awaiting' | 'blocked' | 'neutral'
type OperationalLabel = 'Vendas' | 'Recebimentos'
type OperationalLine = {
  label: OperationalLabel
  status:
    | 'Liberadas'
    | 'Liberados'
    | 'Aguardando conclusão do cadastro'
    | 'Bloqueadas'
    | 'Bloqueados'
    | 'Em análise'
  description: string
  tone: MarkerTone
  reason?: string
}

export function OperationalStatusSection({
  onboarding,
  isSaleBlocked,
}: {
  onboarding: KycOnboarding | null | undefined
  isSaleBlocked?: boolean
}) {
  const registrationFeedback = useFeatureFlag('connectRegistrationFeedback')
  // Before the account exists only the sale restriction can be read locally.
  const fallback = (blocked: boolean | undefined) => (blocked ? 'blocked' : 'waiting')
  const sales = onboardingOperationStatus(
    onboarding?.operations.sales ?? fallback(isSaleBlocked),
    'Vendas',
  )
  const payouts = onboardingOperationStatus(
    onboarding?.operations.payouts ?? 'waiting',
    'Recebimentos',
  )
  const salesFeedback =
    registrationFeedback && sales.tone === 'blocked'
      ? blockedOperationFeedback(
          'Vendas',
          onboarding?.blockedBy.sales ?? (!onboarding && isSaleBlocked ? 'vendepay' : null),
        )
      : {}
  const payoutsFeedback =
    registrationFeedback && payouts.tone === 'blocked'
      ? blockedOperationFeedback('Recebimentos', onboarding?.blockedBy.payouts ?? null)
      : {}
  const headerStatus =
    sales.tone === 'blocked' || payouts.tone === 'blocked'
      ? { label: 'Bloqueado', tone: 'closed' as const }
      : sales.tone === 'ok' && payouts.tone === 'ok'
        ? { label: 'Liberado', tone: 'success' as const }
        : sales.status === 'Em análise' || payouts.status === 'Em análise'
          ? { label: 'Em análise', tone: 'awaiting' as const }
          : { label: 'Aguardando conclusão do cadastro', tone: 'neutral' as const }

  return (
    <Card
      id="situacao-operacional"
      className={formCard}
      data-testid="onboarding-operational-status"
    >
      <section className={section}>
        <div className={sectionHeader}>
          <h2 className={sectionTitle} tabIndex={-1}>
            Situação operacional
          </h2>
          <StatusBadge size="sm" tone={headerStatus.tone} label={headerStatus.label} />
        </div>
        <p className={sectionCopy}>Acompanhe a situação atual das vendas e dos recebimentos.</p>
        <ul className={statusList}>
          <OperationalStatusLine line={{ label: 'Vendas', ...sales, ...salesFeedback }} />
          <OperationalStatusLine line={{ label: 'Recebimentos', ...payouts, ...payoutsFeedback }} />
        </ul>
      </section>
    </Card>
  )
}

function blockedOperationFeedback(
  label: OperationalLabel,
  source: KycOnboarding['blockedBy']['sales'],
): Pick<OperationalLine, 'description' | 'reason'> {
  if (source === 'vendepay') {
    return {
      description:
        label === 'Vendas'
          ? 'Vendas bloqueadas pela VendePay.'
          : 'Recebimentos com restrição cadastral na VendePay.',
      reason: 'Fale com o suporte da VendePay para orientação sobre essa restrição.',
    }
  }
  if (source === 'stripe') {
    return {
      description:
        label === 'Vendas'
          ? 'Vendas bloqueadas por uma restrição na verificação do cadastro.'
          : 'Recebimentos bloqueados por uma restrição na verificação do cadastro.',
      reason: 'Consulte as pendências e a orientação indicadas no cadastro.',
    }
  }
  return {
    description:
      label === 'Vendas' ? 'As vendas estão bloqueadas.' : 'Os recebimentos estão bloqueados.',
    reason: 'Consulte a orientação do cadastro. Se precisar, fale com o suporte da VendePay.',
  }
}

function OperationalStatusLine({ line }: { line: OperationalLine }) {
  return (
    <li className={statusItem}>
      <span className={statusMarker({ tone: line.tone })} data-tone={line.tone} aria-hidden>
        <OperationMarkerIcon tone={line.tone} />
      </span>
      <span className={statusText}>
        <span className={statusTitle}>{line.label}</span>
        <span className={statusValue}>{line.status}</span>
        <span className={statusDescription}>{line.description}</span>
        {line.reason ? <span className={copy}>{line.reason}</span> : null}
      </span>
    </li>
  )
}

function OperationMarkerIcon({ tone }: { tone: MarkerTone }) {
  if (tone === 'ok') return <CheckCircle2 size={15} />
  if (tone === 'awaiting') return <Clock size={15} />
  if (tone === 'neutral') return <Circle size={15} />
  return <X size={15} strokeWidth={2.25} />
}
